const MAX_CSV_BYTES = 2 * 1024 * 1024;

export interface CsvImportReport {
  valid: boolean;
  importId?: string;
  expiresAt?: string;
  rowCount: number;
  uniqueUsers: number;
  duplicateRows: number;
  blankRows: number;
  ignoredColumns: string[];
  errorCount: number;
  errors: Array<{ line: number; code: string; message: string }>;
  sample: Array<{ userId: string; name: string }>;
}

export const decodeCsvBytes = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  if (bytes.length === 0) throw new Error('The CSV file is empty.');
  if (bytes.length > MAX_CSV_BYTES) throw new Error('The CSV file exceeds 2 MiB. Split it into smaller files.');
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) throw new Error('This is a spreadsheet/ZIP file. Export it as CSV; renaming the extension is not enough.');
  let encoding = 'utf-8';
  if (bytes[0] === 0xff && bytes[1] === 0xfe) encoding = 'utf-16le';
  if (bytes[0] === 0xfe && bytes[1] === 0xff) encoding = 'utf-16be';
  let text: string;
  try { text = new TextDecoder(encoding, { fatal: true }).decode(bytes); }
  catch { throw new Error('Unsupported or damaged text encoding. Export as CSV UTF-8. UTF-16 requires a byte-order mark.'); }
  if (!text.trim()) throw new Error('The CSV file is empty.');
  // Control characters here deliberately detect binary or damaged CSV text.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\ufffd]/.test(text)) throw new Error('The file contains binary or invalid text. Export as CSV UTF-8.');
  return text;
};

export const readRecipientCsv = async (file: File, signal?: AbortSignal): Promise<string> => {
  if (!/\.csv$/i.test(file.name)) throw new Error('Select a .csv file. Export Excel workbooks as CSV first.');
  if (file.size === 0) throw new Error('The CSV file is empty.');
  if (file.size > MAX_CSV_BYTES) throw new Error('The CSV file exceeds 2 MiB. Split it into smaller files.');
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    const cleanup = () => signal?.removeEventListener('abort', abort);
    const failed = (message: string) => { cleanup(); reject(new Error(message)); };
    const abort = () => { reader.abort(); failed('File reading was cancelled. Select the CSV again.'); };
    reader.onerror = () => failed('Unable to read the file. Check access permissions and select it again.');
    reader.onabort = () => failed('File reading was cancelled. Select the CSV again.');
    reader.onload = () => {
      cleanup();
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error('Unable to read this file.'));
    };
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener('abort', abort, { once: true });
    try { reader.readAsArrayBuffer(file); }
    catch { failed('Unable to read the file. Check access permissions and select it again.'); }
  });
  return decodeCsvBytes(buffer);
};

/** Escape every field and neutralize spreadsheet formula prefixes in reports. */
export const csvDownloadText = (rows: Array<Array<string | number>>): string => '\ufeff' + rows.map(row => row.map(value => {
  const text = String(value);
  const safe = /^[\s]*[=+@-]/.test(text) || /^[\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}).join(',')).join('\r\n') + '\r\n';

export const downloadCsv = (filename: string, rows: Array<Array<string | number>>) => {
  const url = URL.createObjectURL(new Blob([csvDownloadText(rows)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
};
